import Button from '@prio365/prio365-react-library/lib/Button';
import { InputNumber } from '@prio365/prio365-react-library/lib/Input';
import classNames from 'classnames';
import { useEffect, useState } from 'react';

import { faTrashCan as falTrashCan } from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';
import { FinancialDocumentAllocation } from '../../../../../../models/IncomingInvoices';
import { makePrioStyles } from '../../../../../../theme/utils';
import { getCurrencySymbol } from '../../../../../../util/helpers';
import ProjectPhasePicker from '../../../../../projects/components/ProjectPhasePicker';
import ProjectPickerPrioCL from '../../../../../projects/components/ProjectPickerPrioCL';
import useAccessRights2 from '../../../../../users/hooks/useAccessRights2';
import { useIncomingInvoiceDrawerContext } from './IncomingInvoiceContextProvider';

const useStyles = makePrioStyles((theme) => ({
  root: {
    width: '100%',
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: theme.spacing.regular,
    borderRadius: theme.borderRadius.regular,
    backgroundColor: theme.colors.base.blue[30],
    padding: theme.spacing.regular,
    border: '2px solid transparent',
    transition: 'border-color 0.2s ease',
  },
  invalid: {
    borderColor: theme.colors.base.red[50],
    backgroundColor: theme.colors.base.red[10],
  },
  requiredField: {
    color: theme.colors.base.red[80],
  },
  required: {
    color: theme.colors.base.red[80],
    marginLeft: theme.spacing.extraSmall,
  },
  errorMessage: {
    fontSize: theme.font.fontSize.extraSmall,
    color: theme.colors.base.red[80],
    marginTop: '2px',
    position: 'absolute',
    top: theme.spacing.extraLarge,
    left: '0px',
  },
  trashButtonWrapper: {
    position: 'absolute',
    top: theme.spacing.small,
    right: theme.spacing.regular,
    fontSize: theme.font.fontSize.small,
    zIndex: 1,
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.extraSmall,
    position: 'relative',
  },
  projectWrapper: {
    gridColumn: 'span 2',
  },
  projectPhaseWrapper: {
    gridColumn: '1 / span 3',
  },
  fieldLabel: {
    fontSize: theme.font.fontSize.small,
    color: theme.colors.application.typography.muted,
  },
}));

interface AllocationCardProps {
  className?: string;
  allocation: Partial<FinancialDocumentAllocation>;
  maxAmount: number;
  onChange?: (values: Partial<FinancialDocumentAllocation>) => void;
  onRemove?: () => void;
  allocationStepOptional?: boolean;
}

const AllocationCard = (props: AllocationCardProps) => {
  //#region ------------------------------ Defaults
  const {
    className,
    allocation,
    maxAmount,
    onChange: parentOnChange,
    onRemove,
    allocationStepOptional = false,
  } = props;
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const {
    checkGlobalRoles,
    checkOfficeRolesOfProjectId,
    checkProjectRolesOfProjectId,
  } = useAccessRights2();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const { form } = useIncomingInvoiceDrawerContext();

  const [selectedProject, setSelectedProject] = useState<string | undefined>(
    allocation.projectId || undefined
  );
  const [hasProjectPhases, setHasProjectPhases] = useState<boolean>(false);

  const [validationState, setValidationState] = useState({
    amountValid: true,
    projectValid: true,
  });
  //#endregion

  //#region ------------------------------ Methods
  const getDecimalSeparator = (locale: string): string => {
    try {
      const formatter = new Intl.NumberFormat(locale);
      const formatted = formatter.format(1.1);

      return formatted.includes(',') ? ',' : '.';
    } catch {
      return '.';
    }
  };

  const clampToMaxAmount = (value: number, max: number): number => {
    if (max >= 0) {
      return Math.min(value, max);
    } else {
      return Math.max(value, max);
    }
  };

  const validateAllocation = (
    allocation: Partial<FinancialDocumentAllocation>
  ) => {
    const amountValid = (allocation.amount || 0) !== 0;
    const projectValid = !!allocation.projectId;

    setValidationState({
      amountValid,
      projectValid,
    });

    return amountValid && projectValid;
  };
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    setSelectedProject(allocation.projectId || undefined);
  }, [allocation.projectId]);

  useEffect(() => {
    validateAllocation(allocation);
  }, [allocation]);
  //#endregion

  return (
    <div className={classNames(className, classes.root)}>
      <div className={classes.trashButtonWrapper}>
        <Button
          onClick={() => onRemove?.()}
          type="link"
          iconProp={falTrashCan}
        ></Button>
      </div>

      <div className={classes.contentWrapper}>
        <span
          className={classNames(classes.fieldLabel, {
            [classes.requiredField]: !validationState.amountValid,
          })}
        >
          {t(
            'accounting:incomingInvoices.drawerSteps.allocations.allocationCard.amount'
          )}
          {!allocationStepOptional && (
            <span className={classes.required}>*</span>
          )}{' '}
        </span>
        <InputNumber
          full
          max={maxAmount >= 0 ? maxAmount : undefined}
          min={maxAmount < 0 ? maxAmount : undefined}
          decimalSeparator={getDecimalSeparator(i18n.language)}
          value={allocation?.amount || 0}
          suffixIcon={
            <div>
              {getCurrencySymbol(
                form.getFieldValue('currency') || 'EUR',
                i18n.language
              )}
            </div>
          }
          onChange={(value) => {
            const numericValue =
              typeof value === 'string' ? Number(value) : value;

            const clampedValue = clampToMaxAmount(numericValue || 0, maxAmount);

            const updatedAllocation = {
              ...allocation,
              amount: roundTo(clampedValue, 2),
            };
            parentOnChange?.(updatedAllocation);
          }}
          onBlur={() => {
            const currentValue = allocation.amount ?? 0;
            const clampedValue = clampToMaxAmount(currentValue, maxAmount);

            parentOnChange?.({
              ...allocation,
              amount: roundTo(clampedValue, 2),
            });
          }}
        />
        {!validationState.amountValid && !allocationStepOptional && (
          <span className={classes.errorMessage}>
            {t(
              'accounting:incomingInvoices.drawerSteps.allocations.allocationCard.validationErrors.amountRequired'
            )}
          </span>
        )}
      </div>
      <div
        className={classNames(classes.contentWrapper, classes.projectWrapper)}
      >
        <span
          className={classNames(classes.fieldLabel, {
            [classes.requiredField]: !validationState.projectValid,
          })}
        >
          {t(
            'accounting:incomingInvoices.drawerSteps.allocations.allocationCard.project'
          )}
          {!allocationStepOptional && (
            <span className={classes.required}>*</span>
          )}
        </span>
        <ProjectPickerPrioCL
          value={selectedProject ? [selectedProject] : []}
          label={t(
            'accounting:incomingInvoices.drawerSteps.allocations.allocationCard.projectLabel'
          )}
          size="default"
          filter={(project) =>
            checkGlobalRoles(['globalController']) ||
            checkOfficeRolesOfProjectId(
              ['officeController'],
              project.projectId
            ) ||
            checkProjectRolesOfProjectId(
              ['projectController'],
              project.projectId
            )
          }
          onChange={(value) => {
            const projectIdValue = Array.isArray(value)
              ? (value[0] ?? undefined)
              : value;
            setSelectedProject(projectIdValue);

            const updatedAllocation = {
              ...allocation,
              projectId: projectIdValue,
              projectPhaseId: undefined,
            };
            parentOnChange?.(updatedAllocation);
          }}
        />
        {!validationState.projectValid && !allocationStepOptional && (
          <span className={classes.errorMessage}>
            {t(
              'accounting:incomingInvoices.drawerSteps.allocations.allocationCard.validationErrors.projectRequired'
            )}
          </span>
        )}
      </div>
      <div
        className={classNames(
          classes.contentWrapper,
          classes.projectPhaseWrapper
        )}
      >
        <span className={classes.fieldLabel}>
          {t(
            'accounting:incomingInvoices.drawerSteps.allocations.allocationCard.projectPhase'
          )}
        </span>
        <ProjectPhasePicker
          disabled={!selectedProject || !hasProjectPhases}
          projectId={selectedProject}
          value={allocation.projectPhaseId}
          onChange={(value) => {
            const projectPhaseIdValue = Array.isArray(value)
              ? (value[0] ?? undefined)
              : value;

            const updatedAllocation = {
              ...allocation,
              projectPhaseId: projectPhaseIdValue,
            };
            parentOnChange?.(updatedAllocation);
          }}
          setHasProjectPhases={setHasProjectPhases}
        />
      </div>
    </div>
  );
};

export const roundTo = (value: number, decimals: number = 2): number => {
  const factor = Math.pow(10, decimals);
  return Math.round((value + Number.EPSILON) * factor) / factor;
};

export default AllocationCard;
